import type { Translation } from "../types";

const t: Translation = {
  nav: { trackOrder: "Track Order", track: "Track" },
  hero: {
    title: "Grow Your TikTok",
    titleHighlight: "Audience",
    subtitle: "Professional social media promotion services to help your content reach more people.",
    placeholder: "Enter your TikTok username",
    cta: "Get Started",
    securePayments: "Secure Payments",
    fastProcessing: "Fast Processing",
    noPassword: "No Password Required",
    noUsersFound: "No users found",
    tryDifferent: "Try a different username",
  },
  valueProp: {
    title: "Why Choose",
    titleHighlight: "TokDadddy",
    subtitle: "A trusted platform for TikTok content promotion and audience development.",
    audienceGrowth: "Audience Growth",
    audienceGrowthDesc: "Professional promotion services designed to help your content reach more viewers.",
    fastProcessing: "Fast Processing",
    fastProcessingDesc: "Orders are processed promptly. Results typically visible within 24–72 hours.",
    privacyFirst: "Privacy First",
    privacyFirstDesc: "We never request your login credentials. Your account security is our priority.",
    securePayments: "Secure Payments",
    securePaymentsDesc: "Multiple payment options available. All transactions are encrypted and PCI-compliant.",
    orderTracking: "Order Tracking",
    orderTrackingDesc: "Monitor your order status in real time from placement to completion.",
    dedicatedSupport: "Dedicated Support",
    dedicatedSupportDesc: "Our support team is available to assist you with any questions.",
  },
  howItWorks: {
    title: "How It Works",
    subtitle: "Get started in under 2 minutes. No password needed — ever.",
    step1Title: "Find Your Profile",
    step1Desc: "Enter your TikTok username and we'll locate your profile instantly.",
    step2Title: "Select Services",
    step2Desc: "Choose from our range of promotion packages and customise your order.",
    step3Title: "Secure Checkout",
    step3Desc: "Complete your purchase with our encrypted payment system.",
    step4Title: "Track Your Order",
    step4Desc: "Monitor your order progress in real time from your confirmation page.",
    stat247: "24/7",
    statSSL: "SSL",
    statNo: "No",
    statDelivery: "24–72h",
    statSupportLabel: "Customer Support",
    statEncryptedLabel: "Encrypted Payments",
    statPasswordLabel: "Password Required",
    statDeliveryLabel: "Delivery Window",
  },
  footer: {
    faqTitle: "Frequently Asked Questions",
    faq1Q: "Is my account safe?",
    faq1A: "Absolutely. We never request your password or login credentials. Only your public username is needed to process orders.",
    faq2Q: "How long does processing take?",
    faq2A: "Orders begin processing promptly. Results are typically visible within 24–72 hours depending on the service selected.",
    faq3Q: "What payment methods do you accept?",
    faq3A: "We accept major credit/debit cards, PayPal, and cryptocurrency. All transactions are encrypted and PCI-compliant.",
    faq4Q: "What is your refund policy?",
    faq4A: "We offer refunds for undelivered orders. Please visit our Refund Policy page or contact hello@tokdadddy.com for details.",
    privacy: "Privacy",
    terms: "Terms",
    refundPolicy: "Refund Policy",
    contact: "Contact",
    copyright: "tokdadddy.com. All rights reserved. We are not affiliated with TikTok or ByteDance.",
    visaMastercard: "Visa / Mastercard",
    stripeSecure: "Stripe Secure",
    crypto: "Crypto",
    sslEncrypted: "SSL Encrypted",
  },
  currency: { disclaimer: "Charged in USD. Local price is for reference only." },
  createOrder: {
    followers: "Followers",
    likes: "Likes",
    views: "Views",
    shares: "Shares",
    comments: "Comments",
    selectPostsNextStep: "(select posts in next step)",
    total: "Total",
    discount: "Discount",
    continue: "Continue",
    contactUs: "If you have any questions for our team, please send us a message any time at",
    selected: "Selected",
    posts: "Posts",
  },
  order: {
    totalDueToday: "Total Due Today",
    totalDue: "Total Due",
    selectVideos: "Select videos to promote",
    selectVideosDesc: "Choose which videos you'd like to boost",
    noVideosFound: "No videos found for this user.",
    continueWithVideos: "Continue with {count} video{s}",
    selectAtLeastOne: "Select at least one video",
    oneTimeOffer: "One Time Offer 🔥",
    addFollowers: "Add 5,000 TikTok Followers for only",
    applied: "Applied",
    apply: "Apply",
    paymentFailed: "Payment failed",
    paymentDeclined: "Your payment was declined. Please try one of the other payment methods below.",
    tryCard: "💳 Try Card",
    tryStripe: "💳 Try Stripe",
    tryCrypto: "₿ Try Crypto",
    nameOnCard: "Name on Card",
    cardNumber: "Card Number",
    expiry: "Expiry",
    cvv: "CVV",
    processing: "Processing...",
    pay: "Pay",
    stripeDesc: "Pay securely via Stripe Checkout. Supports cards, Apple Pay, Google Pay, and more.",
    payViaStripe: "Pay via Stripe",
    redirectingStripe: "Redirecting to Stripe...",
    stripePowered: "🔒 Powered by Stripe — PCI Level 1 Certified",
    cryptoDesc: "Pay with Bitcoin, USDT, ETH, and 20+ other cryptocurrencies via Cryptomus.",
    payWithCrypto: "Pay with Crypto",
    secureCryptomus: "🔒 Secure payment via Cryptomus",
    agreeTerms: "You agree to our",
    terms: "Terms",
    refundPolicy: "Refund Policy",
    safeCheckout: "Safe Checkout",
    sslEncrypted: "SSL Encrypted",
    guaranteed: "Tracked Delivery",
    backToVideos: "← Back to video selection",
    videos: "videos",
    bonus: "Bonus",
    selected: "selected",
    orderSummary: "Order Summary",
    minimumOrder: "Minimum order is $0.50",
    invalidCardNumber: "Please enter a valid card number",
    invalidExpiry: "Invalid expiry (MM/YY)",
    invalidCvv: "Invalid CVV",
    cardPaymentFailed: "Card payment failed.",
    cardPaymentDeclined: "Card payment was declined.",
    stripeError: "Stripe error",
    stripePaymentFailed: "Stripe payment failed.",
    paypalFailed: "PayPal failed.",
    cryptoPaymentFailed: "Crypto payment failed.",
    tryPayPal: "🅿️ Try PayPal",
    cardLabel: "Card",
    stripeLabel: "Stripe",
    paypalLabel: "PayPal",
    cryptoLabel: "Crypto",
    stripeDescShort: "Pay securely via Stripe — supports all major credit & debit cards.",
    paypalDesc: "Pay securely with your PayPal account — fast and easy checkout.",
    payWithStripe: "Pay with Stripe",
    payWithPayPal: "Pay with PayPal",
  },
  thankYou: {
    awaitingPayment: "Awaiting Payment",
    awaitingPaymentDesc: "Your order has been created. We're waiting for payment confirmation.",
    thankYou: "Thank You!",
    orderConfirmed: "Your order has been confirmed and is being processed.",
    referenceNumber: "Reference Number",
    copy: "Copy",
    copied: "Copied!",
    payment: "Payment",
    delivery: "Delivery",
    services: "Services",
    total: "Total",
    whatsNext: "What happens next?",
    step1: "Your order is being reviewed and processed",
    step2: "Results will start appearing on your account gradually",
    step3: "Full delivery completes within 24-72 hours",
    bookmark: "Bookmark this page to check your order status anytime — no login required.",
    placeAnother: "Place Another Order",
    pending: "Pending",
    paid: "Paid",
    completed: "Completed",
    processingStatus: "Processing",
    delivered: "Delivered",
    failed: "Failed",
  },
  dashboard: {
    trackYourOrder: "Track Your Order",
    trackDesc: "Enter your reference number or view your recent orders below.",
    searchPlaceholder: "e.g. TD-ABC12345",
    noOrderFound: "No order found for that reference number.",
    yourOrderHistory: "Your Order History",
    refresh: "Refresh",
    noHistory: "No order history yet. Place an order to see it here!",
    placeAnother: "Place Another Order",
    bookmark: "Bookmark this page to check your order status anytime — no login required.",
    whatsNext: "What happens next?",
    step1: "Your order is being reviewed and processed",
    step2: "Results will start appearing on your account gradually",
    step3: "Full delivery completes within 24-72 hours",
  },
  heroByPlatform: {
    tiktok: { title: "Promote your", highlight: "TikTok content", subtitle: "Grow your reach on TikTok with marketing services that help your videos get discovered." },
    youtube: { title: "Grow your", highlight: "YouTube channel", subtitle: "Promote your videos and channel with marketing services designed to expand your audience." },
    spotify: { title: "Promote your", highlight: "Spotify music", subtitle: "Get your tracks in front of new listeners with promotion services for artists and labels." },
    instagram: { title: "Boost your", highlight: "Instagram presence", subtitle: "Promote your profile, posts and reels with marketing services to expand your audience." },
    twitch: { title: "Grow your", highlight: "Twitch stream", subtitle: "Promote your channel and streams with marketing services that help you reach new viewers." },
    twitter: { title: "Promote your", highlight: "X profile", subtitle: "Expand your reach on X with promotion services for tweets, profiles and content campaigns." },
    facebook: { title: "Promote your", highlight: "Facebook page", subtitle: "Grow your Facebook page, posts and videos with marketing services to reach a wider audience." },
  },
};

export default t;
